import API from "../../api/Routes";
import ACTION from "../../actions";
import { API_URL } from "../../config";
import {
  setItem,
  showErrorToast,
  showSuccessToast,
} from "../../utils/Functions";
import ApiServices from "../../services/Api.services";

/******************* 
@Purpose : Used to start loading
@Parameter : dispatch
@Author : INIC
******************/
export const startLoading = () => async (dispatch) => {
  dispatch({
    type: ACTION.START_LOADING,
  });
};
/******************* 
@Purpose : Used to stop loading
@Parameter : dispatch
@Author : INIC
******************/
export const stopLoading = () => async (dispatch) => {
  dispatch({
    type: ACTION.STOP_LOADING,
  });
};

/******************* 
  @Purpose : Used to display Candidate Profile details
  @Parameter : payload
  @Author : INIC
  ******************/
export const offeredCandidateProfile = (payload, silent = false) => async (dispatch) => {
  try {
    if (!silent) dispatch(startLoading());
    await ApiServices.getApi(
      API_URL + API.OFFERCANDIDATE_PROFILE + payload
    ).then((response) => {
      dispatch({
        type: ACTION.OFFERCANDIDATE_ACTION_PROFILE,
        OfferedCandidateProfileDetails: response.data.data,
      });
      if (!silent) dispatch(stopLoading());
    });
  } catch (error) {
    if (!silent) dispatch(stopLoading()); // Always stop loading, even on error
    showErrorToast(error);
  }
};
/******************* 
  @Purpose : Used to Update Candidate Profile details
  @Parameter : payload
  @Author : INIC
  ******************/
export const updateOfferedCandidateProfile = (payload, silent = false) => async (dispatch) => {
  try {
    dispatch(startLoading());
    const response = await ApiServices.postApi(
      API_URL + API.UPDATE_OFFERCANDIDATE_PROFILE,
      payload
    );
    if (!silent) showSuccessToast(response.message);
    // Await the re-fetch so Redux store updates in real-time
    await dispatch(offeredCandidateProfile(payload.id));
  } catch (error) {
    dispatch(stopLoading());
    showErrorToast(error);
  }
};

/******************* 
  @Purpose : Used to submit guest candidate form
  @Parameter : payload
  @Author : INIC
  ******************/
export const guestCandidateForm =
  (payload, photoFormData, photo, resumeFormData, resume, cb) =>
    async (dispatch) => {
      try {
        dispatch(startLoading());
        await ApiServices.postApi(
          API_URL + API.GUEST_CANDIDATE_FORM,
          payload
        ).then((response) => {
          if (response.status === 1) {
            dispatch(
              getCandidateAuthToken(
                payload.id,
                photoFormData,
                photo,
                resumeFormData,
                resume,
                () =>
                  setTimeout(() => {
                    cb();
                  }, 500)
              )
            );
            dispatch(stopLoading());
          } else {
            dispatch(stopLoading()); // Stop loading so skeleton clears on API error
            showErrorToast(response.message);
          }
        });
      } catch (error) {
        dispatch(stopLoading()); // Stop loading on exception too
        showErrorToast(error);
      }
    };

/******************* 
  @Purpose : Used to get candidate auth token
  @Parameter : payload
  @Author : INIC
  ******************/
export const getCandidateAuthToken =
  (payload, photoFormData, photo, resumeFormData, resume, cb) =>
    async (dispatch) => {
      try {
        dispatch(startLoading());
        await ApiServices.postApi(API_URL + API.GET_CANDIDATE_AUTH_TOKEN, {
          guestUserId: payload,
        }).then(async (response) => {
          setItem("accessToken", response.data.token);
          setItem("type", response.data.type);
          setItem("User", JSON.stringify(response.data.data));

          // Set userId only if valid FormData (photo is optional)
          if (resumeFormData instanceof FormData) {
            resumeFormData.set("userId", response.data.data._id);
          }
          if (photoFormData instanceof FormData) {
            photoFormData.set("userId", response.data.data._id);
          }

          // Clean up old temp files (safe to call with empty string)
          if (resume) {
            dispatch(
              tempFileDelete(resume.replace(process.env.REACT_APP_IMG_URL, ""))
            );
          }
          if (photo) {
            dispatch(
              tempFileDelete(photo.replace(process.env.REACT_APP_IMG_URL, ""))
            );
          }

          // Upload resume (required)
          if (resumeFormData instanceof FormData) {
            dispatch(fileUpload(resumeFormData, false));
          }

          // Upload photo (optional)
          if (photoFormData instanceof FormData) {
            await dispatch(fileUpload(photoFormData, false));
          }

          showSuccessToast("Details updated successfully");
          dispatch(stopLoading());
          cb();
        });
      } catch (error) {
        dispatch(stopLoading()); // Always stop loading, even on error
        showErrorToast(error);
      }
    };
/******************* 
  @Purpose : Used to GET Qualification Details
  @Parameter : 
  @Author : INIC
  ******************/
export const getQualificationDetails = () => async (dispatch) => {
  try {
    await ApiServices.getApi(API_URL + API.GET_QUALIFICATION_DETAILS).then(
      (response) => {
        dispatch({
          type: ACTION.QUALIFICATION_DETAIL_LIST,
          qualificationList: response.data.data,
        });
      }
    );
  } catch (error) {
    showErrorToast(error);
  }
};

/******************* 
  @Purpose : Used to GET Skill Set List
  @Parameter : 
  @Author : INIC
  ******************/
export const getSkillSetList = () => async (dispatch) => {
  try {
    await ApiServices.getApi(API_URL + API.GET_SKILL_SET_LIST).then(
      (response) => {
        dispatch({
          type: ACTION.SKILL_SET_LIST,
          skillSetList: response.data.data,
        });
      }
    );
  } catch (error) {
    showErrorToast(error);
  }
};

/******************* 
  @Purpose : Used to GET Country Details
  @Parameter : 
  @Author : INIC
  ******************/
export const getCountryDetails = () => async (dispatch) => {
  try {
    await ApiServices.getApi(API_URL + API.GET_COUNTRY_DETAILS).then(
      (response) => {
        dispatch({
          type: ACTION.COUNTRY_DETAIL_LIST,
          countryDetailList: response.data.data,
        });
      }
    );
  } catch (error) {
    showErrorToast(error);
  }
};

/******************* 
  @Purpose : Used to Delete Resume
  @Parameter : 
  @Author : INIC
  ******************/
export const fileDelete = (payload, cb) => async (dispatch) => {
  try {
    await ApiServices.postApi(
      API_URL + API.DELETE_CANDIDATE_RESUME,
      payload
    ).then((response) => {
      cb();
    });
  } catch (error) {
    showErrorToast(error);
  }
};

export const deleteProfilePhoto = (id, filePath, cb) => async (dispatch) => {
  try {
    dispatch(startLoading());
    dispatch(
      fileDelete({ id, type: "photo", filePath, fileUpdate: false }, async () => {
        showSuccessToast("Profile picture removed successfully");
        await dispatch(offeredCandidateProfile(id));
        if (cb) cb();
      })
    );
  } catch (error) {
    dispatch(stopLoading());
    showErrorToast(error);
  }
};

/******************* 
  @Purpose : Used to Upload Resume
  @Parameter : 
  @Author : INIC
  ******************/
export const fileUpload =
  (payload, isShow = true, cb) =>
    async (dispatch) => {
      try {
        await ApiServices.postApi(
          API_URL + API.UPLOAD_CANDIDATE_RESUME,
          payload
        ).then((response) => {
          isShow && showSuccessToast(response.message);
          cb();
        });
      } catch (error) {
        showErrorToast(error);
      }
    };

/******************* 
  @Purpose : Used to UPload Candidate Resume In Profile
  @Parameter : 
  @Author : INIC
  ******************/
export const uploadOfferedCandidateProfileResume =
  (payload, id, filePath, fileType, fileUpdate, cb) => async (dispatch) => {
    try {
      dispatch(
        fileDelete({ id, type: fileType, filePath, fileUpdate }, () => {
          dispatch(
            fileUpload(payload, true, async () => {
              // Silent refresh: do not toggle loading here. When "Resume + profile
              // data" is selected, addCvDetails owns the skeleton until fetchInfo
              // finishes — a non-silent profile refetch would clear it early.
              await dispatch(offeredCandidateProfile(id, true));
              cb();
            })
          );
        })
      );
    } catch (error) {
      showErrorToast(error);
    }
  };

/******************* 
  @Purpose : Used to Delete Resume
  @Parameter : 
  @Author : INIC
  ******************/
export const tempFileDelete = (payload, cb) => async (dispatch) => {
  // If no existing file to delete, skip the API call and proceed directly
  if (!payload || payload === "" || payload === null || payload === undefined) {
    if (cb) cb();
    return;
  }
  let url =
    payload !== null
      ? payload.replace(process.env.REACT_APP_IMG_URL, "")
      : payload;
  try {
    await ApiServices.deleteApi(
      API_URL + API.DELETE_TEMPORARY_FILE + `?filePath=${url}`
    ).then((response) => {
      if (cb) cb();
    });
  } catch (error) {
    // Still continue upload/replace flow even if old temp file delete fails
    if (cb) cb();
    showErrorToast(error);
  }
};

/******************* 
  @Purpose : Used to Upload Resume
  @Parameter : 
  @Author : INIC
  ******************/
export const tempFileUpload = (payload, cb) => async (dispatch) => {
  try {
    await ApiServices.postApi(
      API_URL + API.UPLOAD_TEMPORARY_FILE,
      payload
    ).then((response) => {
      showSuccessToast(response.message);
      cb(response);
    });
  } catch (error) {
    showErrorToast(error);
  }
};

/******************* 
  @Purpose : Used for UPload temporary file for bulk upload
  @Parameter : payload
  @Author : INIC
  ******************/

export const uploadTemporaryFile =
  (payload, resume, cb) => async (dispatch) => {
    try {
      dispatch(
        tempFileDelete(resume, () => {
          dispatch(
            tempFileUpload(payload, (response) => {
              cb(response);
            })
          );
        })
      );
    } catch (error) {
      showErrorToast(error);
    }
  };

/******************* 
  @Purpose : Used for delete temporary file for add profile
  @Parameter : payload
  @Author : INIC
  ******************/

export const deleteTemporaryFile = (payload) => async (dispatch) => {
  try {
    const response = await ApiServices.deleteApi(
      API_URL + API.DELETE_TEMPORARY_FILE + `?filePath=${payload}`
    );
    if (response.data.status === 1) {
      return response;
    } else if (response.data.status === 0) {
      return response;
    }
    return response;
  } catch (error) {
    if (payload.toast === true) {
      showErrorToast(error);
    }
  }
};

/******************* 
  @Purpose : Used to GET Help desk data
  @Parameter : 
  @Author : INIC
  ******************/
export const getHelpDeskData = () => async (dispatch) => {
  try {
    await ApiServices.getApi(API_URL + API.GET_HELP_DESK_DATA).then(
      (response) => {
        dispatch({
          type: ACTION.OFFEREDCANDIDATE_HELP_DESK,
          helpDeskData: response.data.data,
        });
      }
    );
  } catch (error) {
    showErrorToast(error);
  }
};

/******************* 
  @Purpose : Used for add cv details
  @Parameter : payload
  @Author : INIC
  ******************/

export const addCvDetails = (payload) => async (dispatch) => {
  try {
    // Dispatch action object directly (synchronous) so Skeleton loaders
    // appear immediately before the API call begins
    dispatch({ type: ACTION.START_LOADING });
    const response = await ApiServices.postApi(
      API_URL + API.GET_CANDIDATE_CV_DETAILS,
      payload
    );
    if (response.status === 1) {
      dispatch({
        type: ACTION.GET_CANDIDATE_CV_DETAILS,
        CvInfo: response.data,
      });
      // stopLoading is dispatched by AddProfile/Profile after cvInfo is processed
    } else {
      showErrorToast(response.message || "Failed to parse CV");
      dispatch({ type: ACTION.STOP_LOADING });
    }
    return response;
  } catch (error) {
    showErrorToast(error);
    dispatch({ type: ACTION.STOP_LOADING });
  }
};

/******************* 
@Purpose : Used for work preference master
@Parameter : payload
@Author : INIC
******************/

export const preferenceListing = () => async (dispatch) => {
  try {
    await ApiServices.getApi(
      API_URL + API.MASTER_WORK_PREFERENCE_API_GET_DETAILS
    ).then((response) => {
      dispatch({
        type: ACTION.MASTER_WORK_PREFERENCE_ACTION_DETAILS_LISTING,
        preferenceDetailedList: response.data,
      });
    });
  } catch (error) {
    showErrorToast(error);
  }
};
